import type { FC, LegacyRef } from 'react';
import React from 'react';
import { injectIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { Box, xcss } from '@atlaskit/primitives';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import { N30, N900, N100, N50, N500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { PRIMARY_COLORS, SECONDARY_COLORS } from '../colors';
import { sharedi18n } from '../sharedi18n';
import { ContentStateColor, ContentStateRestrictionLevel } from '../ContentStateColor';

import { type PageTitleContentStateButtonProps } from './types';
import { i18n } from './i18n';
import { determineSecondaryColor } from './helpers';

type DropdownProps = {
	isSelected?: boolean;
	isWarning?: boolean;
	color: string;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DefaultColor = styled.div({
	width: '16px',
	height: '16px',
	border: `2px solid ${token('color.border.accent.gray', N50)}`,
	borderRadius: '22px',
	marginLeft: token('space.025', '2px'),
});

const defaultColorWrapperStyles = xcss({
	marginRight: 'space.075',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentStateName = styled.div({
	margin: `0 ${token('space.025', '2px')}`,
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChevronDownIconWrapper = styled.div({
	marginRight: token('space.negative.050', '-4px'),
	color: token('color.text.subtle', N500),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NameAndLabelWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	width: '100%',
	alignItems: 'center',
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageTitleStatusDropdownButton = styled.button({
	display: 'flex',
	width: '100%',
	marginRight: token('space.100', '8px'),
	alignItems: 'center',
	height: '40px',
	cursor: 'pointer',
	padding: `0 ${token('space.100', '8px')}`,
	color: token('color.text', N900),
	font: 'inherit',
	fontWeight: token('font.weight.medium'),
	borderRadius: '3px',
	outlineColor: token('color.border.input', N100),
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		border: ({ isWarning, isSelected }: DropdownProps) =>
			!isWarning && !isSelected
				? `2px solid ${token('color.border.input', N100)}`
				: `2px solid ${token('color.background.input.hovered', N30)}`,
		backgroundColor: token('color.background.input.hovered', N30),
	},
	'&:active': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		borderColor: ({ color }: DropdownProps) => color,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		backgroundColor: ({ color }: DropdownProps) => color,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...({ isWarning, isSelected, color }: DropdownProps) =>
		isWarning
			? {
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					border: `2px solid ${PRIMARY_COLORS.RED}`,
					// Disabled linting for SECONDARY_COLOERS.RED because this is being
					// tokenized already in the file where it's defined.
					// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					backgroundColor: SECONDARY_COLORS.RED,
				}
			: {
					border: isSelected
						? `2px solid ${color}`
						: `2px solid ${token('color.border.input', N100)}`,
					backgroundColor: isSelected ? color : 'transparent',
				},
});

const PageTitleContentStateButtonComponent: FC<PageTitleContentStateButtonProps> = ({
	triggerProps,
	togglePopup,
	contentState,
	isSelected,
	isWarning,
	userOffline,
	contentStateButtonRef,
	intl: { formatMessage },
}) => {
	const primaryColor = contentState?.color ?? PRIMARY_COLORS.BLUE;
	const secondaryColor = determineSecondaryColor(primaryColor);

	const isContentStateSelectorDisabled = contentState?.isCallerPermitted === false || userOffline;
	const isContentStateRestricted =
		contentState?.restrictionLevel ===
		ContentStateRestrictionLevel[ContentStateRestrictionLevel.PAGE_OWNER];
	const tooltipContent = isContentStateRestricted
		? sharedi18n.verifiedRestricted
		: i18n.changeStatus;

	return (
		<div ref={contentStateButtonRef as LegacyRef<HTMLDivElement>}>
			<Tooltip content={formatMessage(tooltipContent)} position="bottom">
				<PageTitleStatusDropdownButton
					{...triggerProps}
					appearance="subtle"
					onClick={togglePopup}
					isSelected={isSelected}
					disabled={isContentStateSelectorDisabled}
					isWarning={isWarning}
					color={secondaryColor}
					data-testid="page-title-content-state-trigger"
					type="button"
				>
					{contentState ? (
						<ContentStateColor
							dataTestId="button-color-icon"
							appearance="pageTitleContentStateButton"
							color={primaryColor}
							restrictionLevel={contentState?.restrictionLevel}
							isWarning={isWarning}
						/>
					) : (
						<Box xcss={defaultColorWrapperStyles}>
							<DefaultColor data-testid="button-color-icon" />
						</Box>
					)}
					<NameAndLabelWrapper>
						<ContentStateName>
							{contentState ? contentState?.name : formatMessage(i18n.statusLabel)}
						</ContentStateName>
						<ChevronDownIconWrapper>
							<ChevronDownIcon
								color="currentColor"
								LEGACY_size="medium"
								spacing="spacious"
								label=""
							/>
						</ChevronDownIconWrapper>
					</NameAndLabelWrapper>
				</PageTitleStatusDropdownButton>
			</Tooltip>
		</div>
	);
};

export const PageTitleContentStateButton = injectIntl(PageTitleContentStateButtonComponent);
